<template>
    <q-table
        :rows="rows"
        :columns="columns"
        :rows-per-page-options="[null]"
        :no-data-label="$t('No records found for this date range.')"
        :hide-bottom="true"
        dense
        class="sticky-column-table full-width bg-secondary-white no-shadow border-grey no-border-top"
        title-class="text-weight-medium text-medium-grey"
        row-key="index"
    >
        <!-- Header slot -->
        <template v-slot:header="props">
            <q-tr :props="props" class="bg-primary text-white header-height">
                <q-th :key="props.cols[0].name" :props="props">
                    <span class="text-weight-bold" style="font-size: 14px">
                        {{ props.cols[0].label }}
                    </span>
                </q-th>
                <q-th v-for="col in visibleCols" :key="col.name" :props="props">
                    <span class="text-weight-bold" style="font-size: 14px">
                        {{ col.label }}
                    </span>
                </q-th>
                <q-th
                    :key="props.cols[props.cols.length - 1].name"
                    :props="props"
                >
                    <span class="text-weight-bold" style="font-size: 14px">
                        {{ props.cols[props.cols.length - 1].label }}
                    </span>
                </q-th>
            </q-tr>
        </template>

        <!-- Top row for pagination buttons -->
        <template v-slot:top-row>
            <q-tr v-if="isChunked">
                <q-td class="bg-grey-4 no-padding"></q-td>
                <q-td class="bg-grey-4 no-padding">
                    <q-btn
                        dense
                        flat
                        :disable="colChunkStart === 1"
                        @click="prevColChunk"
                        color="primary"
                    >
                        <q-icon name="chevron_left" size="16px" />
                        <span class="navigation-button-label">
                            {{ $t('Previous') }}
                        </span>
                    </q-btn>
                </q-td>
                <q-td
                    v-for="n in visibleCols.length - 2"
                    :key="n"
                    class="bg-grey-4 no-padding"
                ></q-td>
                <q-td class="bg-grey-4 text-right no-padding">
                    <q-btn
                        dense
                        flat
                        :disable="colChunkEnd >= columns.length - 2"
                        @click="nextColChunk"
                        color="primary"
                    >
                        <q-icon name="chevron_right" size="16px" />
                        <span class="navigation-button-label">
                            {{ $t('Next') }}
                        </span>
                    </q-btn>
                </q-td>
                <q-td class="bg-grey-4 no-padding"></q-td>
            </q-tr>
        </template>

        <!-- Row slot -->
        <template v-slot:body="props">
            <!-- Group Header -->
            <q-tr :props="props" :class="getGroupHeaderClass(props.row)">
                <q-td
                    :colspan="props.cols.length"
                    @click="props.row.expand = !props.row.expand"
                    key="group_name"
                    :props="props"
                    class="cursor-pointer group-name-row"
                >
                    <div class="group-name-label">
                        {{ props.row.group_name }}
                        <q-icon
                            name="keyboard_arrow_down"
                            class="text-medium-grey"
                            :class="{ 'rotate-180': props.row.expand }"
                        />
                    </div>
                </q-td>
            </q-tr>
            <!-- Detail Rows -->
            <q-tr
                v-show="props.row.expand && props.row.details"
                v-for="(detail, index) in props.row.details"
                :key="detail.name || index"
            >
                <q-td>
                    <div class="text-left">
                        {{ detail.name }}
                    </div>
                </q-td>
                <q-td v-for="col in visibleCols" :key="col.name" :props="props">
                    <div class="text-right">
                        {{
                            formatNumberByType(
                                detail[col.name],
                                props.row.group_type
                            ) ?? '-'
                        }}
                    </div>
                </q-td>
                <q-td class="total">
                    <div class="text-right text-bold">
                        {{
                            formatNumberByType(
                                detail.total,
                                props.row.group_type
                            ) ?? $t('No Data')
                        }}
                    </div>
                </q-td>
            </q-tr>
        </template>
    </q-table>
</template>

<script>
import {
    formatPercentage,
    formatProductionValue
} from '@utils/numberFormatters.js';
import { mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'TrendsReportTable',
    props: {
        rows: {
            type: Array,
            required: true
        },
        columns: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            colChunkIndex: 0,
            chunkSize: 48
        };
    },
    computed: {
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * The starting index (1-based) of the current visible column chunk (excluding first and last columns).
         * @returns {number}
         */
        colChunkStart() {
            return 1 + this.colChunkIndex * this.chunkSize;
        },
        /**
         * The ending index (1-based) of the current visible column chunk (excluding first and last columns).
         * @returns {number}
         */
        colChunkEnd() {
            const total = this.columns.length - 2;
            const end = (this.colChunkIndex + 1) * this.chunkSize;
            return end > total ? total : end;
        },
        /**
         * Returns the currently visible chunk of columns for the table body, excluding the first (row label) and last (total) columns.
         * The first and last columns are handled separately and not included in the body chunk.
         * @returns {Array<Object>} Array of visible column objects for the current chunk, excluding the last column.
         */
        visibleCols() {
            // Always show the first and last columns (row label and total) separately
            // Only chunk the columns in the middle, excluding the last column
            if (this.columns.length <= 2) return [];
            const start = 1 + this.colChunkIndex * this.chunkSize;
            const end = Math.min(
                1 + (this.colChunkIndex + 1) * this.chunkSize,
                this.columns.length - 1 // Exclude the last column
            );
            return this.columns.slice(start, end);
        },
        /**
         * Determines if the columns are chunked (i.e., the number of columns requires navigation).
         * @returns {boolean} True if chunking is active, false otherwise.
         */
        isChunked() {
            // Subtract 2 for the first and last columns (row label and total)
            return this.columns.length > this.chunkSize + 2;
        }
    },
    methods: {
        /**
         * Navigate to the previous chunk of columns.
         * @returns {void}
         */
        prevColChunk() {
            if (this.colChunkIndex > 0) {
                this.colChunkIndex--;
            }
        },
        /**
         * Navigate to the next chunk of columns.
         * @returns {void}
         */
        nextColChunk() {
            const totalChunks = Math.ceil(
                (this.columns.length - 2) / this.chunkSize
            );
            if (this.colChunkIndex < totalChunks - 1) {
                this.colChunkIndex++;
            }
        },
        formatProductionValue,
        /**
         * Get the group header class based on the group type. (OEE metrics vs. Product group)
         * @param {*} row
         * @returns {string}
         */
        getGroupHeaderClass(row) {
            switch (row.group_type) {
                case 'oee_metrics':
                    return 'bg-light-blue text-weight-bold overall-header';
                case 'total_production':
                    return 'bg-light-blue text-weight-bold overall-header';
                case 'product':
                    return 'bg-lighter-grey';
                default:
                    return '';
            }
        },
        /**
         * Format number by value type.
         * @param {number} value
         * @param {string} formattedValue
         */
        formatNumberByType(value, type) {
            switch (type) {
                case 'oee_metrics':
                    return value !== null
                        ? formatPercentage(value, this.userLocale)
                        : null;
                default:
                    return value !== null && value !== undefined
                        ? this.formatProductionValue(value, this.userLocale)
                        : null;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.q-table th,
.q-table td {
    min-width: 120px;
    white-space: pre-line;
}
.header-height {
    height: 40px !important;
}

.group-name-label {
    width: 600px;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0 15px;
}

.no-border-top {
    border-top: none;
}

.sticky-column-table {
    // Asset Name Slot
    thead tr:first-child th:first-child {
        background-color: $primary;
        padding-left: 15px;
    }

    // --- Title column --- //

    // Sticky asset name column
    th:first-child,
    td:first-child {
        position: sticky;
        left: 0;
        z-index: 1;
    }

    // Remove group name row padding to align with asset name column (regardless of y-scroll)
    td:first-child {
        padding-left: 0px;
    }

    // Sticky rows (not group name row)
    td:first-child:not(.group-name-row) {
        /* bg color is important for th; just specify one */
        background-color: #f2f2f2;
        border-right: 1px solid $light-grey;
        padding-left: 15px;
        white-space: nowrap;
    }

    // Sticky group name row
    tr td:first-child .group-name-label {
        position: sticky;
        left: 0;
        z-index: 3;
    }

    // --- Summary column --- //
    th:last-child,
    td:last-child {
        position: sticky;
        right: 0;
        z-index: 1;
    }

    // Remove group name row padding to align with asset name column (regardless of y-scroll)
    td:last-child {
        padding-right: 0px;
    }

    // Sticky rows (not group name row)
    td:last-child:not(.group-name-row) {
        /* bg color is important for th; just specify one */
        background-color: #f2f2f2;
        border-left: 1px solid $light-grey;
        padding-right: 15px;
    }

    // Sticky group name row
    tr td:last-child .group-name-label {
        position: sticky;
        right: 0;
        z-index: 3;
    }
}

.no-padding {
    padding: 0 !important;
}

.navigation-button-label {
    font-size: 14px;
    font-weight: 400;
    color: $primary;
}
</style>
